import { RankKit } from "@/components/site/RankKit";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section, CTABand } from "@/components/site/Blocks";
import { STATE_RATES } from "@/data/stateTaxRates";
import { buildHead } from "@/lib/seo-head";
import { ToolLeadGate } from "@/components/site/ToolLeadGate";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";
import { DealComparisonSections } from "@/components/site/DealComparisonSections";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";
import brrrrHero from "@/assets/hero/hero-brrrr-calculator-screen.jpg";


export const Route = createFileRoute("/brrrr-calculator")({
  head: () =>
    buildHead({
      title: "BRRRR Calculator — Buy, Rehab, Rent, Refi | Simply Approved",
      description:
        "Free BRRRR calculator: model purchase, rehab, ARV, DSCR refi, and cash left in the deal. See cash-on-cash, DSCR, and infinite-return potential.",
      url: "/brrrr-calculator",
      keywords:
        "BRRRR calculator, buy rehab rent refinance repeat calculator, BRRRR method calculator, ARV calculator, cash left in deal calculator, BRRRR refinance calculator, DSCR refi calculator, rehab budget calculator, infinite return real estate, BRRRR strategy tool, rental property refinance calculator",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "BRRRR Calculator", url: "/brrrr-calculator" },
      ],
      service: { name: "BRRRR Strategy Calculator" },
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "BRRRR Calculator",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          url: "/brrrr-calculator",
          description:
            "Free BRRRR calculator modeling purchase, rehab, ARV, DSCR refinance, and cash left in the deal.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        },
      ],
    }),
  component: Brrrr,
});

type Inputs = {
  // Acquisition
  purchasePrice: number;
  rehabBudget: number;
  arv: number;
  holdMonths: number;
  // Bridge / hard-money purchase loan
  bridgeLtv: number; // % of purchase covered
  bridgeRehabPct: number; // % of rehab financed
  bridgeRate: number;
  bridgePoints: number;
  bridgeClosingCosts: number;
  // Refi (DSCR)
  refiLtv: number;
  refiRate: number;
  refiTermYears: number;
  refiClosingCostsPct: number;
  // Operations
  monthlyRent: number;
  stateCode: string;
  taxesAnnual: number;
  insAnnual: number;
  hoaMonthly: number;
  vacancyPct: number;
  mgmtPct: number;
  maintenancePct: number;
};

const DEFAULT: Inputs = {
  purchasePrice: 180000,
  rehabBudget: 45000,
  arv: 300000,
  holdMonths: 6,
  bridgeLtv: 85,
  bridgeRehabPct: 100,
  bridgeRate: 10.5,
  bridgePoints: 2,
  bridgeClosingCosts: 4000,
  refiLtv: 75,
  refiRate: 7.75,
  refiTermYears: 30,
  refiClosingCostsPct: 3,
  monthlyRent: 2400,
  stateCode: "GA",
  taxesAnnual: 3000,
  insAnnual: 1400,
  hoaMonthly: 0,
  vacancyPct: 5,
  mgmtPct: 8,
  maintenancePct: 5,
};

function amortPayment(principal: number, aprPct: number, years: number) {
  if (principal <= 0) return 0;
  const r = aprPct / 100 / 12;
  const n = years * 12;
  if (r <= 0) return principal / n;
  return (principal * (r * Math.pow(1 + r, n))) / (Math.pow(1 + r, n) - 1);
}

function Brrrr() {
  const [i, setI] = useState<Inputs>(DEFAULT);
  const set = <K extends keyof Inputs>(k: K, v: Inputs[K]) => setI((p) => ({ ...p, [k]: v }));

  const m = useMemo(() => {
    // ----- Acquisition -----
    const bridgePurchase = i.purchasePrice * (i.bridgeLtv / 100);
    const bridgeRehab = i.rehabBudget * (i.bridgeRehabPct / 100);
    const bridgeLoan = bridgePurchase + bridgeRehab;
    const pointsCost = bridgeLoan * (i.bridgePoints / 100);
    const bridgeInterestDuringHold = bridgeLoan * (i.bridgeRate / 100) * (i.holdMonths / 12);
    const cashDownAtPurchase =
      i.purchasePrice - bridgePurchase + (i.rehabBudget - bridgeRehab) + pointsCost + i.bridgeClosingCosts;
    const totalProjectCost = i.purchasePrice + i.rehabBudget + pointsCost + i.bridgeClosingCosts + bridgeInterestDuringHold;

    // ----- Refi -----
    const refiLoan = i.arv * (i.refiLtv / 100);
    const refiClosingCosts = refiLoan * (i.refiClosingCostsPct / 100);
    const cashOutAtRefi = refiLoan - bridgeLoan - bridgeInterestDuringHold - refiClosingCosts;
    // Cash left = total cash out of pocket − cash returned at refi
    const cashLeftInDeal = cashDownAtPurchase - cashOutAtRefi;

    // ----- Ongoing (post-refi) -----
    const pi = amortPayment(refiLoan, i.refiRate, i.refiTermYears);
    const taxesMo = i.taxesAnnual / 12;
    const insMo = i.insAnnual / 12;
    const pitia = pi + taxesMo + insMo + i.hoaMonthly;
    const vacancy = i.monthlyRent * (i.vacancyPct / 100);
    const mgmt = i.monthlyRent * (i.mgmtPct / 100);
    const maint = i.monthlyRent * (i.maintenancePct / 100);
    const effectiveIncome = i.monthlyRent - vacancy;
    const noiMo = effectiveIncome - mgmt - maint - taxesMo - insMo - i.hoaMonthly;
    const cashFlowMo = noiMo - pi;

    // ----- Metrics -----
    const dscr = pitia > 0 ? i.monthlyRent / pitia : 0;
    const capRate = i.arv > 0 ? (noiMo * 12) / i.arv : 0;
    const coc = cashLeftInDeal > 0 ? (cashFlowMo * 12) / cashLeftInDeal : cashLeftInDeal <= 0 ? Infinity : 0;
    const arvSpread = i.arv - totalProjectCost;
    const arvSpreadPct = i.arv > 0 ? arvSpread / i.arv : 0;
    const isInfiniteBrrrr = cashLeftInDeal <= 0;
    const seventyRule = i.arv * 0.7 - i.rehabBudget; // max purchase per 70% rule

    return {
      bridgeLoan,
      pointsCost,
      bridgeInterestDuringHold,
      cashDownAtPurchase,
      totalProjectCost,
      refiLoan,
      refiClosingCosts,
      cashOutAtRefi,
      cashLeftInDeal,
      pi,
      pitia,
      cashFlowMo,
      dscr,
      capRate,
      coc,
      arvSpread,
      arvSpreadPct,
      isInfiniteBrrrr,
      seventyRule,
    };
  }, [i]);

  const dscrColor = m.dscr >= 1.2 ? "text-emerald-600" : m.dscr >= 1.0 ? "text-amber-600" : "text-destructive";
  const coCColor = m.coc === Infinity ? "text-emerald-600" : m.coc >= 0.12 ? "text-emerald-600" : m.coc >= 0.08 ? "text-amber-600" : "text-destructive";

  const refiScenarioParam = btoa(
    JSON.stringify({
      mode: "ltr",
      price: Math.round(i.arv),
      downPct: 100 - i.refiLtv,
      rate: i.refiRate,
      termYears: i.refiTermYears,
      stateCode: i.stateCode,
      taxesAnnual: i.taxesAnnual,
      insAnnual: i.insAnnual,
      hoaMonthly: i.hoaMonthly,
      ltrRent: i.monthlyRent,
      vacancyPct: i.vacancyPct,
      mgmtPct: i.mgmtPct,
    }),
  )
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

  return (
    <SiteShell>
      <PageHero
        eyebrow="BRRRR calculator"
        title="Buy, Rehab, Rent, Refinance, Repeat"
        subtitle="Model the full BRRRR loop — bridge-loan acquisition, rehab, and DSCR cash-out refinance — and see exactly how much cash stays in the deal."
        primaryHref="/apply"
        primaryLabel="Start My Approval"
        secondaryHref="/rates"
        secondaryLabel="See Today's Rates"
        image={{
          src: brrrrHero,
          alt: "Real estate investor reviewing BRRRR deal numbers on a laptop inside a light-renovation rental property",
        }}
      />
      <RankKit page="brrrr-calculator" part="answer" />

      <PageEssentials hideAnswer />

      <Section>
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1.4fr_1fr]">
          {/* Inputs */}
          <div className="space-y-6">
            <Card title="1. Acquisition">
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Purchase price" value={i.purchasePrice} onChange={(v) => set("purchasePrice", v)} prefix="$" />
                <Field label="Rehab budget" value={i.rehabBudget} onChange={(v) => set("rehabBudget", v)} prefix="$" />
                <Field label="After-Repair Value (ARV)" value={i.arv} onChange={(v) => set("arv", v)} prefix="$" />
                <Field label="Hold time before refi (months)" value={i.holdMonths} onChange={(v) => set("holdMonths", v)} step={1} />
              </div>
              <div className="mt-3 rounded-md border border-brand-gold/40 bg-brand-gold/5 p-3 text-xs text-foreground">
                <strong>70% rule check:</strong> max all-in purchase ≈ ${m.seventyRule.toLocaleString()} (ARV × 70% − rehab).{" "}
                {i.purchasePrice <= m.seventyRule ? "✅ Deal passes." : "⚠️ Above the 70% rule — margin is thin."}
              </div>
            </Card>

            <Card title="2. Bridge / hard-money loan">
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Purchase LTV %" value={i.bridgeLtv} onChange={(v) => set("bridgeLtv", v)} suffix="%" step={1} />
                <Field label="Rehab financed %" value={i.bridgeRehabPct} onChange={(v) => set("bridgeRehabPct", v)} suffix="%" step={1} />
                <Field label="Rate (interest-only)" value={i.bridgeRate} onChange={(v) => set("bridgeRate", v)} suffix="%" step={0.125} />
                <Field label="Origination points" value={i.bridgePoints} onChange={(v) => set("bridgePoints", v)} suffix="pts" step={0.25} />
                <Field label="Bridge closing costs" value={i.bridgeClosingCosts} onChange={(v) => set("bridgeClosingCosts", v)} prefix="$" />
              </div>
            </Card>

            <Card title="3. DSCR refinance">
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Refi LTV %" value={i.refiLtv} onChange={(v) => set("refiLtv", v)} suffix="%" step={1} />
                <Field label="Refi rate" value={i.refiRate} onChange={(v) => set("refiRate", v)} suffix="%" step={0.125} />
                <Field label="Term (years)" value={i.refiTermYears} onChange={(v) => set("refiTermYears", v)} step={1} />
                <Field label="Refi closing costs" value={i.refiClosingCostsPct} onChange={(v) => set("refiClosingCostsPct", v)} suffix="%" step={0.25} />
              </div>
            </Card>

            <Card title="4. Rent & expenses (after refi)">
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Monthly rent" value={i.monthlyRent} onChange={(v) => set("monthlyRent", v)} prefix="$" />
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-foreground">State</span>
                  <select
                    value={i.stateCode}
                    onChange={(e) => set("stateCode", e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-base"
                  >
                    {STATE_RATES.map((r) => (
                      <option key={r.code} value={r.code}>{r.name}</option>
                    ))}
                  </select>
                </label>
                <Field label="Taxes (annual)" value={i.taxesAnnual} onChange={(v) => set("taxesAnnual", v)} prefix="$" />
                <Field label="Insurance (annual)" value={i.insAnnual} onChange={(v) => set("insAnnual", v)} prefix="$" />
                <Field label="HOA (monthly)" value={i.hoaMonthly} onChange={(v) => set("hoaMonthly", v)} prefix="$" />
                <Field label="Vacancy" value={i.vacancyPct} onChange={(v) => set("vacancyPct", v)} suffix="%" step={1} />
                <Field label="Management" value={i.mgmtPct} onChange={(v) => set("mgmtPct", v)} suffix="%" step={1} />
                <Field label="Maintenance / capex" value={i.maintenancePct} onChange={(v) => set("maintenancePct", v)} suffix="%" step={1} />
              </div>
            </Card>
          </div>

          {/* Results */}
          <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
            <ToolLeadGate
              storageKey="brrrr-calc:lead-unlocked"
              product="BRRRR Calculator"
              sourcePage="BRRRR Calculator"
              title="Unlock your BRRRR deal analysis"
              subtitle="See DSCR, cash-on-cash, cash-left-in-deal, and the full loan breakdown — plus we'll email you a copy."
              getContext={() => ({
                purchase_price: i.purchasePrice,
                rehab_budget: i.rehabBudget,
                arv: i.arv,
                monthly_rent: i.monthlyRent,
                state: i.stateCode,
                dscr: m.dscr.toFixed(2),
                cash_left_in_deal: Math.round(m.cashLeftInDeal),
                refi_loan: Math.round(m.refiLoan),
              })}
            >
              <div className="space-y-4">
                <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
                  <div className="text-xs font-semibold uppercase tracking-widest text-brand-gold">Deal snapshot</div>
                  <div className="mt-1 font-display text-2xl font-bold text-foreground">
                    {m.isInfiniteBrrrr ? "Infinite-return BRRRR ✨" : "Traditional BRRRR"}
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {m.isInfiniteBrrrr
                      ? "Cash-out refi returns 100%+ of your capital — you're building a rental with no money left in the deal."
                      : `You'll leave $${Math.max(0, Math.round(m.cashLeftInDeal)).toLocaleString()} in the deal after refi.`}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Stat label="DSCR (rent ÷ PITIA)" value={m.dscr.toFixed(2)} colorClass={dscrColor} />
                  <Stat label="Cash-on-cash" value={m.coc === Infinity ? "∞" : `${(m.coc * 100).toFixed(1)}%`} colorClass={coCColor} />
                  <Stat label="Cap rate (on ARV)" value={`${(m.capRate * 100).toFixed(2)}%`} />
                  <Stat label="Monthly cash flow" value={`$${Math.round(m.cashFlowMo).toLocaleString()}`} colorClass={m.cashFlowMo >= 0 ? "text-emerald-600" : "text-destructive"} />
                </div>

                <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
                  <h3 className="font-display text-base font-semibold text-foreground">Cash & loan breakdown</h3>
                  <dl className="mt-3 space-y-1.5 text-sm">
                    <Row label="All-in project cost" value={`$${Math.round(m.totalProjectCost).toLocaleString()}`} />
                    <Row label="Bridge loan (purchase + rehab)" value={`$${Math.round(m.bridgeLoan).toLocaleString()}`} />
                    <Row label="Points cost" value={`$${Math.round(m.pointsCost).toLocaleString()}`} />
                    <Row label={`Bridge interest (${i.holdMonths} mo hold)`} value={`$${Math.round(m.bridgeInterestDuringHold).toLocaleString()}`} />
                    <Row label="Cash out of pocket at purchase" value={`$${Math.round(m.cashDownAtPurchase).toLocaleString()}`} strong />
                    <Row label={`DSCR refi loan (${i.refiLtv}% of ARV)`} value={`$${Math.round(m.refiLoan).toLocaleString()}`} />
                    <Row label="Refi closing costs" value={`$${Math.round(m.refiClosingCosts).toLocaleString()}`} />
                    <Row
                      label="Cash returned at refi"
                      value={`$${Math.round(m.cashOutAtRefi).toLocaleString()}`}
                      strong
                      colorClass={m.cashOutAtRefi >= 0 ? "text-emerald-600" : "text-destructive"}
                    />
                    <Row
                      label="Cash left in deal"
                      value={`$${Math.round(m.cashLeftInDeal).toLocaleString()}`}
                      strong
                      colorClass={m.cashLeftInDeal <= 0 ? "text-emerald-600" : "text-foreground"}
                    />
                    <Row label="ARV spread over all-in" value={`$${Math.round(m.arvSpread).toLocaleString()} (${(m.arvSpreadPct * 100).toFixed(1)}%)`} />
                    <Row label="Monthly PI (refi)" value={`$${Math.round(m.pi).toLocaleString()}`} />
                    <Row label="Monthly PITIA (refi)" value={`$${Math.round(m.pitia).toLocaleString()}`} />
                  </dl>
                </div>

                <a
                  href={`/dscr-calculator?s=${refiScenarioParam}`}
                  className="block rounded-md bg-brand px-4 py-3 text-center text-sm font-semibold text-primary-foreground hover:bg-brand/90"
                >
                  Model this refi in the DSCR calculator →
                </a>
                <Link
                  to="/apply"
                  className="block rounded-md border border-brand-gold bg-brand-gold px-4 py-3 text-center text-sm font-semibold text-primary hover:bg-brand-gold-hover"
                >Start My Approval</Link>
              </div>
            </ToolLeadGate>
          </aside>
        </div>
      </Section>

      <RankKit page="brrrr-calculator" part="details" />
      <LiveDscrRateSection
        strategyCards
        id="live-rates"
        eyebrow={"Live pricing · DSCR"}
        heading={"Live DSCR pricing for the refinance leg"}
        paragraphs={[
          "These are actual pricing responses returned for a sample business-purpose DSCR scenario on non-owner-occupied investment property — rate, APR, borrower points or lender credit, estimated principal and interest, and lock period from the same quote. Pricing changes intraday and is not a loan approval, commitment, or guarantee of financing.",
        ]}
        links={[
          { to: "/investment-property-rates", label: "Price your own DSCR scenario" },
          { to: "/rates", label: "See today's investor rate bands" },
        ]}
      />

      <Section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-4xl">
          <h2 className="font-display text-3xl font-bold text-foreground">How the BRRRR loop works</h2>
          <ol className="mt-6 space-y-4 text-sm text-foreground/90">
            <li><strong>Buy</strong> a distressed property under market using a bridge / fix-and-flip loan (typically 85–90% of purchase + 100% of rehab).</li>
            <li><strong>Rehab</strong> to force appreciation. The gap between your all-in cost and ARV is where BRRRR profit is created.</li>
            <li><strong>Rent</strong> the property at market to establish DSCR and season the asset.</li>
            <li><strong>Refinance</strong> with a DSCR loan at 75% of ARV. If ARV is high enough, the refi pays off the bridge and returns most or all of your cash.</li>
            <li><strong>Repeat</strong> with the same capital on the next deal.</li>
          </ol>
        </div>
      </Section>

      <Section className="border-t border-border bg-muted/30">
        <DealComparisonSections />
      </Section>

      <CTABand
        title="Ready to close a BRRRR?"
        body="We pair a fix-and-flip bridge with a same-lender DSCR refi so you don't lose your rate lock between phases."
      />
    <PageOutro />
      </SiteShell>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
      <h3 className="font-display text-lg font-semibold text-foreground">{title}</h3>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  prefix,
  suffix,
  step = 100,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  prefix?: string;
  suffix?: string;
  step?: number;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-foreground">{label}</span>
      <div className="flex items-center rounded-md border border-input bg-background focus-within:border-brand">
        {prefix && <span className="pl-3 text-sm text-muted-foreground">{prefix}</span>}
        <input
          type="number"
          value={Number.isFinite(value) ? value : 0}
          step={step}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className="w-full bg-transparent px-3 py-2 text-base tabular-nums outline-none"
        />
        {suffix && <span className="pr-3 text-sm text-muted-foreground">{suffix}</span>}
      </div>
    </label>
  );
}

function Stat({ label, value, colorClass = "text-foreground" }: { label: string; value: string; colorClass?: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1 font-display text-2xl font-bold tabular-nums ${colorClass}`}>{value}</div>
    </div>
  );
}

function Row({
  label,
  value,
  strong,
  colorClass = "text-foreground",
}: {
  label: string;
  value: string;
  strong?: boolean;
  colorClass?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={`tabular-nums ${strong ? "font-semibold" : ""} ${colorClass}`}>{value}</dd>
    </div>
  );
}
